import React, { useState } from "react";
import { CheckCircle2, XCircle, HelpCircle } from "lucide-react";
import { recordDiagramQuiz, getDiagramResult } from "../lib/store";

// Schémas SVG thématisés (s'adaptent au clair/sombre et au niveau Bleu/Ambre).
const C = {
  primary: "hsl(var(--primary))",
  border: "hsl(var(--border))",
  muted: "hsl(var(--muted-foreground))",
  fg: "hsl(var(--foreground))",
  success: "hsl(var(--success))",
  warning: "hsl(var(--warning))",
  card: "hsl(var(--card))",
};

function Frame({ caption, children, viewBox = "0 0 420 240" }) {
  return (
    <figure className="mt-5 rounded-sm border border-border bg-secondary/30 p-4">
      <svg viewBox={viewBox} className="w-full h-auto" role="img" style={{ maxHeight: 260 }}>
        {children}
      </svg>
      {caption && (
        <figcaption className="text-xs text-muted-foreground text-center mt-3 font-mono">
          {caption}
        </figcaption>
      )}
    </figure>
  );
}

const T = (props) => (
  <text fontSize="11" fontFamily="ui-monospace, monospace" fill={C.muted} {...props} />
);

/* Dipôle demi-onde : rayonnement perpendiculaire au fil (figure en 8). */
function Dipole() {
  return (
    <Frame caption="Dipôle λ/2 : le rayonnement est maximal perpendiculairement au fil.">
      {/* lobes de rayonnement (figure en 8, verticaux) */}
      <ellipse cx="210" cy="70" rx="42" ry="55" fill={C.primary} opacity="0.14" />
      <ellipse cx="210" cy="170" rx="42" ry="55" fill={C.primary} opacity="0.14" />
      {/* fil horizontal (deux brins) */}
      <line x1="70" y1="120" x2="203" y2="120" stroke={C.fg} strokeWidth="3" />
      <line x1="217" y1="120" x2="350" y2="120" stroke={C.fg} strokeWidth="3" />
      {/* point d'alimentation */}
      <circle cx="210" cy="120" r="4" fill={C.primary} />
      {/* coax vers le bas */}
      <line x1="210" y1="124" x2="210" y2="215" stroke={C.primary} strokeWidth="2.5" />
      {/* flèches de rayonnement */}
      <line x1="210" y1="118" x2="210" y2="40" stroke={C.primary} strokeWidth="1.5" markerEnd="url(#ar)" />
      <line x1="210" y1="122" x2="210" y2="200" stroke={C.primary} strokeWidth="1.5" markerEnd="url(#ar)" />
      <defs>
        <marker id="ar" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
          <path d="M0,0 L6,3 L0,6 Z" fill={C.primary} />
        </marker>
      </defs>
      <T x="70" y="112">λ/2 (demi-onde)</T>
      <T x="216" y="212" fill={C.primary}>Coax 50 Ω</T>
      <T x="248" y="70" fill={C.primary}>Rayonnement max ⊥</T>
    </Frame>
  );
}

/* Antenne verticale : rayonnement tout autour (omnidirectionnel). */
function Vertical() {
  return (
    <Frame caption="Antenne verticale : rayonnement omnidirectionnel autour du mât.">
      {/* sol */}
      <line x1="40" y1="180" x2="380" y2="180" stroke={C.border} strokeWidth="2" />
      {/* radians (plan de sol) */}
      <line x1="210" y1="180" x2="120" y2="192" stroke={C.muted} strokeWidth="1.5" />
      <line x1="210" y1="180" x2="300" y2="192" stroke={C.muted} strokeWidth="1.5" />
      {/* mât vertical */}
      <line x1="210" y1="180" x2="210" y2="55" stroke={C.fg} strokeWidth="3" />
      {/* lobes omnidirectionnels (deux ellipses latérales) */}
      <ellipse cx="130" cy="118" rx="60" ry="34" fill={C.primary} opacity="0.14" />
      <ellipse cx="290" cy="118" rx="60" ry="34" fill={C.primary} opacity="0.14" />
      <line x1="210" y1="110" x2="90" y2="110" stroke={C.primary} strokeWidth="1.5" markerEnd="url(#av)" />
      <line x1="210" y1="110" x2="330" y2="110" stroke={C.primary} strokeWidth="1.5" markerEnd="url(#av)" />
      <defs>
        <marker id="av" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
          <path d="M0,0 L6,3 L0,6 Z" fill={C.primary} />
        </marker>
      </defs>
      <T x="216" y="55">Élément vertical</T>
      <T x="150" y="205">Plan de sol / radians</T>
      <T x="150" y="100" fill={C.primary}>Rayonne dans toutes les directions</T>
    </Frame>
  );
}

/* Coupe d'un câble coaxial. */
function Coax() {
  return (
    <Frame caption="Câble coaxial : âme centrale, isolant, tresse (blindage), gaine." viewBox="0 0 420 220">
      <circle cx="150" cy="110" r="85" fill={C.card} stroke={C.fg} strokeWidth="2.5" />
      <circle cx="150" cy="110" r="66" fill="none" stroke={C.muted} strokeWidth="6" strokeDasharray="4 3" />
      <circle cx="150" cy="110" r="48" fill={C.primary} opacity="0.10" stroke={C.border} strokeWidth="1.5" />
      <circle cx="150" cy="110" r="12" fill={C.primary} />
      {/* lignes de légende */}
      <line x1="150" y1="110" x2="300" y2="60" stroke={C.border} strokeWidth="1" />
      <line x1="192" y1="90" x2="300" y2="95" stroke={C.border} strokeWidth="1" />
      <line x1="210" y1="110" x2="300" y2="130" stroke={C.border} strokeWidth="1" />
      <line x1="232" y1="140" x2="300" y2="165" stroke={C.border} strokeWidth="1" />
      <T x="304" y="63" fill={C.primary}>Âme (conducteur central)</T>
      <T x="304" y="98">Isolant (diélectrique)</T>
      <T x="304" y="133">Tresse (blindage)</T>
      <T x="304" y="168">Gaine extérieure</T>
    </Frame>
  );
}

/* Trois modes de propagation. */
function PropagationModes() {
  return (
    <Frame caption="Onde de sol, onde directe (à vue) et onde de ciel (via l'ionosphère).">
      {/* ionosphère */}
      <path d="M20,42 Q210,18 400,42" fill="none" stroke={C.primary} strokeWidth="2" strokeDasharray="6 5" />
      <T x="165" y="34" fill={C.primary}>Ionosphère</T>
      {/* terre */}
      <path d="M0,215 Q210,150 420,215" fill={C.success} opacity="0.12" />
      <path d="M0,215 Q210,150 420,215" fill="none" stroke={C.border} strokeWidth="2" />
      {/* station A */}
      <line x1="70" y1="176" x2="70" y2="150" stroke={C.fg} strokeWidth="2.5" />
      <T x="52" y="196">Station</T>
      {/* onde directe */}
      <line x1="72" y1="150" x2="150" y2="164" stroke={C.fg} strokeWidth="2" markerEnd="url(#ap)" />
      <T x="86" y="146">directe</T>
      <line x1="150" y1="176" x2="150" y2="164" stroke={C.fg} strokeWidth="2.5" />
      {/* onde de sol */}
      <path d="M74,155 Q140,205 250,196" fill="none" stroke={C.warning} strokeWidth="2" markerEnd="url(#ap)" />
      <T x="150" y="205" fill={C.warning}>onde de sol</T>
      {/* onde de ciel */}
      <path d="M76,148 L210,46" stroke={C.primary} strokeWidth="2" markerEnd="url(#ap)" />
      <path d="M210,46 L360,168" stroke={C.primary} strokeWidth="2" markerEnd="url(#ap)" />
      <T x="230" y="90" fill={C.primary}>onde de ciel</T>
      <line x1="360" y1="180" x2="360" y2="168" stroke={C.fg} strokeWidth="2.5" />
      <defs>
        <marker id="ap" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
          <path d="M0,0 L6,3 L0,6 Z" fill={C.muted} />
        </marker>
      </defs>
    </Frame>
  );
}

/* Réflexion ionosphérique (skywave / distance de saut). */
function Ionosphere() {
  return (
    <Frame caption="L'ionosphère réfléchit les ondes HF vers la Terre : liaisons de milliers de km.">
      <path d="M20,50 Q210,26 400,50" fill={C.primary} opacity="0.10" />
      <path d="M20,50 Q210,26 400,50" fill="none" stroke={C.primary} strokeWidth="2" strokeDasharray="6 5" />
      <T x="160" y="42" fill={C.primary}>Ionosphère (ionisée par le Soleil)</T>
      {/* terre */}
      <path d="M0,220 Q210,150 420,220" fill={C.success} opacity="0.12" />
      <path d="M0,220 Q210,150 420,220" fill="none" stroke={C.border} strokeWidth="2" />
      {/* émetteur */}
      <line x1="60" y1="184" x2="60" y2="158" stroke={C.fg} strokeWidth="2.5" />
      <T x="40" y="204">Émetteur</T>
      {/* rayon montant + réflexion */}
      <line x1="62" y1="156" x2="210" y2="60" stroke={C.primary} strokeWidth="2.2" markerEnd="url(#ai)" />
      <line x1="210" y1="60" x2="360" y2="170" stroke={C.primary} strokeWidth="2.2" markerEnd="url(#ai)" />
      {/* récepteur */}
      <line x1="360" y1="184" x2="360" y2="170" stroke={C.fg} strokeWidth="2.5" />
      <T x="325" y="204">Récepteur</T>
      {/* distance de saut */}
      <line x1="60" y1="212" x2="360" y2="212" stroke={C.warning} strokeWidth="1.5" strokeDasharray="3 3" />
      <T x="150" y="226" fill={C.warning}>Distance de saut (skip)</T>
      <defs>
        <marker id="ai" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
          <path d="M0,0 L6,3 L0,6 Z" fill={C.primary} />
        </marker>
      </defs>
    </Frame>
  );
}

/* Portée VHF/UHF en ligne de vue : antenne plus haute = plus loin. */
function VhfLineOfSight() {
  return (
    <Frame caption="VHF/UHF : portée en ligne de vue. Antenne plus haute = horizon plus lointain.">
      {/* courbure de la Terre */}
      <path d="M0,230 Q210,120 420,230" fill={C.success} opacity="0.12" />
      <path d="M0,230 Q210,120 420,230" fill="none" stroke={C.border} strokeWidth="2" />
      {/* tour haute (gauche) */}
      <line x1="80" y1="182" x2="80" y2="70" stroke={C.fg} strokeWidth="3" />
      <circle cx="80" cy="70" r="4" fill={C.primary} />
      <T x="46" y="60" fill={C.primary}>Antenne haute</T>
      {/* tour basse (droite) */}
      <line x1="340" y1="150" x2="340" y2="118" stroke={C.fg} strokeWidth="3" />
      <circle cx="340" cy="118" r="4" fill={C.primary} />
      <T x="300" y="110">Antenne basse</T>
      {/* ligne de vue tangente à l'horizon */}
      <path d="M80,70 Q210,150 340,118" fill="none" stroke={C.primary} strokeWidth="2" strokeDasharray="7 4" markerEnd="url(#al)" />
      <T x="150" y="120" fill={C.primary}>Ligne de vue</T>
      {/* horizon */}
      <circle cx="210" cy="150" r="3" fill={C.warning} />
      <T x="185" y="172" fill={C.warning}>Horizon</T>
      <defs>
        <marker id="al" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
          <path d="M0,0 L6,3 L0,6 Z" fill={C.primary} />
        </marker>
      </defs>
    </Frame>
  );
}

/* Symbole de diode : anode -> cathode (le courant circule dans le sens +x local). */
function Diode({ x, y, angle }) {
  return (
    <g transform={`translate(${x},${y}) rotate(${angle})`}>
      <polygon points="-8,-7 -8,7 6,0" fill={C.primary} />
      <line x1="6" y1="-8" x2="6" y2="8" stroke={C.primary} strokeWidth="2.5" />
    </g>
  );
}

/* Redresseur en pont : 4 diodes, entrée alternative, sortie continue. */
function BridgeRectifier() {
  return (
    <Frame caption="Pont de 4 diodes : les deux alternances sont redressées → sortie continue." viewBox="0 0 480 260">
      <defs>
        <marker id="brA" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
          <path d="M0,0 L6,3 L0,6 Z" fill={C.muted} />
        </marker>
      </defs>
      {/* losange (fils) */}
      <line x1="165" y1="120" x2="235" y2="60" stroke={C.fg} strokeWidth="2" />
      <line x1="305" y1="120" x2="235" y2="60" stroke={C.fg} strokeWidth="2" />
      <line x1="235" y1="180" x2="165" y2="120" stroke={C.fg} strokeWidth="2" />
      <line x1="235" y1="180" x2="305" y2="120" stroke={C.fg} strokeWidth="2" />
      {/* diodes */}
      <Diode x={200} y={90} angle={-40.6} />
      <Diode x={270} y={90} angle={-139.4} />
      <Diode x={200} y={150} angle={-139.4} />
      <Diode x={270} y={150} angle={-40.6} />
      {/* noeuds */}
      <circle cx="235" cy="60" r="3.5" fill={C.fg} />
      <circle cx="235" cy="180" r="3.5" fill={C.fg} />
      <circle cx="165" cy="120" r="3.5" fill={C.fg} />
      <circle cx="305" cy="120" r="3.5" fill={C.fg} />
      {/* transformateur (secondaire) */}
      <path d="M92,90 q10,10 0,20 q-10,10 0,20 q10,10 0,20" fill="none" stroke={C.warning} strokeWidth="2.5" />
      <line x1="105" y1="86" x2="105" y2="160" stroke={C.border} strokeWidth="1.5" />
      <line x1="108" y1="86" x2="108" y2="160" stroke={C.border} strokeWidth="1.5" />
      {/* fils entrée AC */}
      <line x1="92" y1="95" x2="130" y2="95" stroke={C.warning} strokeWidth="2" />
      <line x1="130" y1="95" x2="130" y2="120" stroke={C.warning} strokeWidth="2" />
      <line x1="130" y1="120" x2="165" y2="120" stroke={C.warning} strokeWidth="2" />
      <line x1="92" y1="145" x2="92" y2="215" stroke={C.warning} strokeWidth="2" />
      <line x1="92" y1="215" x2="375" y2="215" stroke={C.warning} strokeWidth="2" />
      <line x1="375" y1="215" x2="375" y2="120" stroke={C.warning} strokeWidth="2" />
      <line x1="375" y1="120" x2="305" y2="120" stroke={C.warning} strokeWidth="2" />
      {/* sortie + */}
      <line x1="235" y1="60" x2="235" y2="35" stroke={C.primary} strokeWidth="2" />
      <line x1="235" y1="35" x2="415" y2="35" stroke={C.primary} strokeWidth="2" />
      <line x1="415" y1="35" x2="415" y2="95" stroke={C.primary} strokeWidth="2" />
      {/* sortie - */}
      <line x1="235" y1="180" x2="235" y2="235" stroke={C.fg} strokeWidth="2" />
      <line x1="235" y1="235" x2="415" y2="235" stroke={C.fg} strokeWidth="2" />
      <line x1="415" y1="235" x2="415" y2="150" stroke={C.fg} strokeWidth="2" />
      {/* condensateur de filtrage */}
      <line x1="415" y1="95" x2="415" y2="112" stroke={C.fg} strokeWidth="2" />
      <line x1="402" y1="112" x2="428" y2="112" stroke={C.fg} strokeWidth="2.5" />
      <line x1="402" y1="120" x2="428" y2="120" stroke={C.fg} strokeWidth="2.5" />
      <line x1="415" y1="120" x2="415" y2="150" stroke={C.fg} strokeWidth="2" />
      {/* charge (résistance) */}
      <line x1="415" y1="95" x2="455" y2="95" stroke={C.fg} strokeWidth="2" />
      <rect x="447" y="105" width="16" height="40" rx="2" fill="none" stroke={C.fg} strokeWidth="2" />
      <line x1="415" y1="150" x2="455" y2="150" stroke={C.fg} strokeWidth="2" />
      <T x="60" y="82" fill={C.warning}>Transfo (alternatif)</T>
      <T x="245" y="30" fill={C.primary}>+</T>
      <T x="245" y="252">−</T>
      <T x="360" y="108">C</T>
      <T x="440" y="160">Charge</T>
      <T x="300" y="252" fill={C.primary}>Sortie continue</T>
    </Frame>
  );
}

/* Réseau d'adaptation en pi : 2 condensateurs shunt + 1 bobine série. */
function PiLNetwork() {
  return (
    <Frame caption="Réseau en pi : 2 condensateurs en parallèle + 1 bobine en série (large plage).">
      {/* rail haut */}
      <line x1="45" y1="70" x2="120" y2="70" stroke={C.fg} strokeWidth="2" />
      {/* bobine série (arcs) */}
      <path d="M120,70 q10,-16 20,0 q10,-16 20,0 q10,-16 20,0 q10,-16 20,0" fill="none" stroke={C.primary} strokeWidth="2.5" />
      <line x1="200" y1="70" x2="375" y2="70" stroke={C.fg} strokeWidth="2" />
      {/* C1 shunt (gauche) */}
      <line x1="120" y1="70" x2="120" y2="120" stroke={C.fg} strokeWidth="2" />
      <line x1="104" y1="120" x2="136" y2="120" stroke={C.primary} strokeWidth="2.5" />
      <line x1="104" y1="128" x2="136" y2="128" stroke={C.primary} strokeWidth="2.5" />
      <line x1="120" y1="128" x2="120" y2="165" stroke={C.fg} strokeWidth="2" />
      {/* C2 shunt (droite) */}
      <line x1="300" y1="70" x2="300" y2="120" stroke={C.fg} strokeWidth="2" />
      <line x1="284" y1="120" x2="316" y2="120" stroke={C.primary} strokeWidth="2.5" />
      <line x1="284" y1="128" x2="316" y2="128" stroke={C.primary} strokeWidth="2.5" />
      <line x1="300" y1="128" x2="300" y2="165" stroke={C.fg} strokeWidth="2" />
      {/* masse */}
      <line x1="60" y1="165" x2="360" y2="165" stroke={C.border} strokeWidth="2" />
      <line x1="45" y1="70" x2="45" y2="165" stroke={C.border} strokeWidth="1.5" strokeDasharray="3 3" />
      {/* bornes */}
      <circle cx="45" cy="70" r="3.5" fill={C.fg} />
      <circle cx="375" cy="70" r="3.5" fill={C.fg} />
      <T x="16" y="60">Émetteur</T>
      <T x="24" y="98">50 Ω</T>
      <T x="345" y="60">Antenne</T>
      <T x="150" y="42" fill={C.primary}>L (série)</T>
      <T x="86" y="150" fill={C.primary}>C1</T>
      <T x="322" y="150" fill={C.primary}>C2</T>
      <T x="150" y="182">Masse — Réseau en L = 1 bobine + 1 condensateur (plage limitée)</T>
    </Frame>
  );
}

/* Récepteur superhétérodyne : chaîne de traitement du signal. */
function Superhet() {
  const box = (x, y, w, h, label, sub) => (
    <g>
      <rect x={x} y={y} width={w} height={h} rx="3" fill={C.primary} opacity="0.10" stroke={C.primary} strokeWidth="1.5" />
      <text x={x + w / 2} y={sub ? y + h / 2 - 3 : y + h / 2 + 4} textAnchor="middle" fontSize="10.5" fontFamily="ui-monospace, monospace" fill={C.fg}>{label}</text>
      {sub && <text x={x + w / 2} y={y + h / 2 + 11} textAnchor="middle" fontSize="8.5" fontFamily="ui-monospace, monospace" fill={C.muted}>{sub}</text>}
    </g>
  );
  const arrow = (x1, y1, x2, y2) => <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={C.muted} strokeWidth="1.8" markerEnd="url(#shA)" />;
  return (
    <Frame caption="Superhétérodyne : conversion vers une FI fixe = filtrage optimal et grande sélectivité." viewBox="0 0 480 250">
      <defs>
        <marker id="shA" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
          <path d="M0,0 L6,3 L0,6 Z" fill={C.muted} />
        </marker>
      </defs>
      {/* antenne */}
      <line x1="28" y1="95" x2="28" y2="60" stroke={C.fg} strokeWidth="2" />
      <line x1="18" y1="60" x2="38" y2="60" stroke={C.fg} strokeWidth="2" />
      <T x="8" y="52">Antenne</T>
      {arrow(28, 88, 55, 88)}
      {box(55, 70, 70, 36, "Ampli RF")}
      {arrow(125, 88, 150, 88)}
      {box(150, 70, 70, 36, "Mélangeur")}
      {arrow(220, 88, 245, 88)}
      {box(245, 70, 90, 36, "Ampli FI", "+ filtre à cristal")}
      {/* oscillateur local -> mélangeur */}
      {box(150, 150, 70, 34, "Oscillateur", "local")}
      {arrow(185, 150, 185, 106)}
      {/* FI -> détecteur (route par la droite) */}
      <line x1="335" y1="88" x2="390" y2="88" stroke={C.muted} strokeWidth="1.8" />
      <line x1="390" y1="88" x2="390" y2="190" stroke={C.muted} strokeWidth="1.8" />
      {arrow(390, 190, 362, 190)}
      {box(295, 172, 67, 36, "Détecteur")}
      {arrow(295, 190, 262, 190)}
      {box(190, 172, 72, 36, "Ampli audio")}
      {arrow(190, 190, 160, 190)}
      {/* haut-parleur */}
      <path d="M150,182 h-14 v16 h14 l16,12 v-40 z" fill="none" stroke={C.fg} strokeWidth="1.8" />
      <T x="96" y="222">Haut-parleur</T>
    </Frame>
  );
}

/* Constante de temps RC/RL : charge à 63,2 % après 1 τ. */
function TimeConstant() {
  const pts = [
    [55, 190], [87, 139], [120, 108], [152, 89], [185, 78],
    [217, 71], [250, 66], [315, 62], [380, 61],
  ].map((p) => p.join(",")).join(" ");
  return (
    <Frame caption="Constante de temps τ : 63,2 % de la valeur finale atteinte après 1 τ (≈100 % après 5 τ).">
      {/* axes */}
      <line x1="55" y1="40" x2="55" y2="190" stroke={C.border} strokeWidth="1.5" />
      <line x1="55" y1="190" x2="385" y2="190" stroke={C.border} strokeWidth="1.5" />
      {/* asymptote 100% */}
      <line x1="55" y1="60" x2="380" y2="60" stroke={C.muted} strokeWidth="1.2" strokeDasharray="4 4" />
      {/* repère 63,2% */}
      <line x1="55" y1="108" x2="120" y2="108" stroke={C.success} strokeWidth="1.2" strokeDasharray="4 4" />
      <line x1="120" y1="190" x2="120" y2="108" stroke={C.success} strokeWidth="1.2" strokeDasharray="4 4" />
      {/* courbe de charge */}
      <polyline points={pts} fill="none" stroke={C.primary} strokeWidth="2.5" />
      <circle cx="120" cy="108" r="4" fill={C.success} />
      <T x="128" y="104" fill={C.success}>63,2 %</T>
      <T x="300" y="54">V max (≈100 %)</T>
      <T x="112" y="204">τ</T>
      <T x="360" y="204">5τ</T>
      <T x="300" y="204">temps →</T>
      <T x="60" y="52" fill={C.primary}>Tension / courant</T>
      <T x="130" y="160">Décharge : il reste 36,8 % après 1 τ.</T>
    </Frame>
  );
}

/* Courbe de résonance : facteur Q (bande étroite vs large). */
function Resonance() {
  return (
    <Frame caption="Résonance (Xl = Xc) : un Q élevé donne une bande passante étroite (forte sélectivité).">
      <line x1="55" y1="40" x2="55" y2="190" stroke={C.border} strokeWidth="1.5" />
      <line x1="55" y1="190" x2="385" y2="190" stroke={C.border} strokeWidth="1.5" />
      {/* Q faible (large) */}
      <path d="M75,190 Q210,95 345,190" fill="none" stroke={C.muted} strokeWidth="2" />
      {/* Q élevé (étroit) */}
      <path d="M165,190 Q210,42 255,190" fill="none" stroke={C.primary} strokeWidth="2.5" />
      {/* f0 */}
      <line x1="210" y1="190" x2="210" y2="48" stroke={C.success} strokeWidth="1.2" strokeDasharray="4 4" />
      <circle cx="210" cy="48" r="4" fill={C.primary} />
      <T x="185" y="204" fill={C.success}>f₀ (résonance)</T>
      <T x="258" y="70" fill={C.primary}>Q élevé (bande étroite)</T>
      <T x="270" y="150" fill={C.muted}>Q faible (bande large)</T>
      <T x="60" y="52">Amplitude</T>
      <T x="320" y="204">fréquence →</T>
    </Frame>
  );
}

/* Jonction P-N : zone P (trous) + zone N (électrons) = diode. */
function PnJunction() {
  return (
    <Frame caption="Jonction P-N : zone P (trous, +) accolée à la zone N (électrons, −) = une diode." viewBox="0 0 440 220">
      {/* fil anode */}
      <line x1="30" y1="110" x2="95" y2="110" stroke={C.fg} strokeWidth="2" />
      {/* bloc P */}
      <rect x="95" y="65" width="115" height="90" fill={C.warning} opacity="0.16" stroke={C.warning} strokeWidth="2" />
      <text x="152" y="118" textAnchor="middle" fontSize="30" fontFamily="ui-monospace, monospace" fontWeight="bold" fill={C.warning}>P</text>
      {["120,88", "180,88", "130,138", "175,138"].map((p) => {
        const [x, y] = p.split(",");
        return <text key={p} x={x} y={y} fontSize="14" fill={C.warning} textAnchor="middle">+</text>;
      })}
      {/* bloc N */}
      <rect x="210" y="65" width="115" height="90" fill={C.primary} opacity="0.14" stroke={C.primary} strokeWidth="2" />
      <text x="267" y="118" textAnchor="middle" fontSize="30" fontFamily="ui-monospace, monospace" fontWeight="bold" fill={C.primary}>N</text>
      {["238,88", "298,88", "248,138", "292,138"].map((p) => {
        const [x, y] = p.split(",");
        return <text key={p} x={x} y={y} fontSize="14" fill={C.primary} textAnchor="middle">−</text>;
      })}
      {/* zone de déplétion */}
      <line x1="210" y1="65" x2="210" y2="155" stroke={C.fg} strokeWidth="1.5" strokeDasharray="4 3" />
      {/* fil cathode */}
      <line x1="325" y1="110" x2="410" y2="110" stroke={C.fg} strokeWidth="2" />
      <T x="30" y="100">Anode</T>
      <T x="118" y="180" fill={C.warning}>Trous (+)</T>
      <T x="235" y="180" fill={C.primary}>Électrons (−)</T>
      <T x="370" y="100">Cathode</T>
      <T x="168" y="52">Jonction (zone de déplétion)</T>
    </Frame>
  );
}

/* Transistor bipolaire NPN & PNP : la base commande le courant collecteur-émetteur. */
function Transistor() {
  const sym = (ox, npn) => (
    <g>
      {/* enveloppe */}
      <circle cx={ox + 20} cy="110" r="44" fill="none" stroke={C.border} strokeWidth="1.5" />
      {/* barre de base */}
      <line x1={ox + 15} y1="88" x2={ox + 15} y2="132" stroke={C.fg} strokeWidth="3" />
      {/* fil base */}
      <line x1={ox - 25} y1="110" x2={ox + 15} y2="110" stroke={C.fg} strokeWidth="2" />
      {/* collecteur */}
      <line x1={ox + 15} y1="97" x2={ox + 48} y2="72" stroke={C.fg} strokeWidth="2" />
      <line x1={ox + 48} y1="72" x2={ox + 48} y2="50" stroke={C.fg} strokeWidth="2" />
      {/* émetteur (avec flèche) */}
      {npn ? (
        <line x1={ox + 15} y1="123" x2={ox + 48} y2="148" stroke={C.primary} strokeWidth="2.2" markerEnd="url(#trA)" />
      ) : (
        <line x1={ox + 48} y1="148" x2={ox + 15} y2="123" stroke={C.primary} strokeWidth="2.2" markerEnd="url(#trA)" />
      )}
      <line x1={ox + 48} y1="148" x2={ox + 48} y2="170" stroke={C.fg} strokeWidth="2" />
      {/* bornes */}
      <T x={ox - 34} y="106">B</T>
      <T x={ox + 52} y="46">C</T>
      <T x={ox + 52} y="182">E</T>
      <text x={ox + 20} y="18" textAnchor="middle" fontSize="13" fontFamily="ui-monospace, monospace" fontWeight="bold" fill={C.primary}>{npn ? "NPN" : "PNP"}</text>
      <text x={ox + 20} y="205" textAnchor="middle" fontSize="10" fontFamily="ui-monospace, monospace" fill={C.muted}>{npn ? "N-P-N" : "P-N-P"}</text>
    </g>
  );
  return (
    <Frame caption="Transistor : un faible courant de BASE commande un fort courant Collecteur→Émetteur (amplification ou commutation)." viewBox="0 0 460 220">
      <defs>
        <marker id="trA" markerWidth="9" markerHeight="9" refX="7" refY="3" orient="auto">
          <path d="M0,0 L7,3 L0,6 Z" fill={C.primary} />
        </marker>
      </defs>
      {sym(90, true)}
      {sym(300, false)}
      <T x="200" y="118" fill={C.muted}>La flèche de</T>
      <T x="200" y="132" fill={C.muted}>l'émetteur</T>
      <T x="200" y="146" fill={C.muted}>donne le type</T>
    </Frame>
  );
}

const MAP = {
  dipole: Dipole,
  vertical: Vertical,
  coax: Coax,
  "propagation-modes": PropagationModes,
  ionosphere: Ionosphere,
  "vhf-los": VhfLineOfSight,
  "bridge-rectifier": BridgeRectifier,
  "pi-l-network": PiLNetwork,
  superhet: Superhet,
  "time-constant": TimeConstant,
  resonance: Resonance,
  "pn-junction": PnJunction,
  transistor: Transistor,
};

export function Diagram({ name, section, level }) {
  const Cmp = MAP[name];
  if (!Cmp) return null;
  return (
    <>
      <Cmp />
      <DiagramQuiz name={name} section={section} level={level} />
    </>
  );
}

// Mini-quiz de reconnaissance affiché sous chaque schéma (révélation instantanée).
const DIAGRAM_QUIZ = {
  dipole: { q: "Dans quelle direction un dipôle demi-onde rayonne-t-il le maximum ?", options: ["Perpendiculairement au fil", "Dans l'axe du fil", "Vers le sol uniquement"], answer: 0 },
  vertical: { q: "Comment rayonne une antenne verticale ?", options: ["Dans toutes les directions (omnidirectionnel)", "Vers le nord seulement", "Perpendiculairement au sol"], answer: 0 },
  coax: { q: "Quel élément du câble coaxial assure le blindage ?", options: ["La tresse", "L'âme centrale", "La gaine extérieure"], answer: 0 },
  "propagation-modes": { q: "Quel mode de propagation domine en VHF/UHF ?", options: ["L'onde directe (à vue)", "L'onde de ciel", "L'onde de sol"], answer: 0 },
  ionosphere: { q: "Que fait l'ionosphère aux ondes HF ?", options: ["Elle les réfléchit vers la Terre", "Elle les absorbe totalement", "Elle les accélère"], answer: 0 },
  "vhf-los": { q: "Comment augmenter la portée en VHF/UHF ?", options: ["Élever l'antenne", "Baisser l'antenne", "Réduire la fréquence"], answer: 0 },
  "bridge-rectifier": { q: "Combien de diodes comporte un redresseur en pont ?", options: ["4", "2", "1"], answer: 0 },
  "pi-l-network": { q: "Quel réseau offre la plage d'adaptation la plus large ?", options: ["Le réseau en pi", "Le réseau en L", "Aucun des deux"], answer: 0 },
  superhet: { q: "Quel est l'avantage clé du récepteur superhétérodyne ?", options: ["Une meilleure sélectivité (FI fixe)", "Une plus grande puissance", "Une antenne plus petite"], answer: 0 },
  "time-constant": { q: "Quelle fraction est atteinte après 1 constante de temps (charge) ?", options: ["63,2 %", "36,8 %", "100 %"], answer: 0 },
  resonance: { q: "Un facteur Q élevé donne une bande passante…", options: ["Étroite", "Large", "Nulle"], answer: 0 },
  "pn-junction": { q: "Une seule jonction P-N forme…", options: ["Une diode", "Un transistor", "Une résistance"], answer: 0 },
  transistor: { q: "Qu'est-ce qui distingue un NPN d'un PNP sur le symbole ?", options: ["Le sens de la flèche de l'émetteur", "La couleur du symbole", "Le nombre de bornes"], answer: 0 },
};

function DiagramQuiz({ name, section, level }) {
  const data = DIAGRAM_QUIZ[name];
  const [opts] = useState(() => {
    if (!data) return [];
    const correct = data.options[data.answer];
    const shuffled = [...data.options].sort(() => Math.random() - 0.5);
    return shuffled.map((text) => ({ text, correct: text === correct }));
  });
  const [selected, setSelected] = useState(() => {
    if (!data || !level) return null;
    const stored = getDiagramResult(level, name);
    if (!stored) return null;
    return opts.findIndex((o) => o.text === stored.selected);
  });
  if (!data) return null;

  const choose = (i) => {
    if (selected !== null) return;
    setSelected(i);
    if (level && section) recordDiagramQuiz(level, section, name, opts[i].correct, opts[i].text);
  };

  return (
    <div className="mt-3 rounded-sm border border-border bg-card p-4" data-testid="diagram-quiz">
      <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-primary mb-2">
        <HelpCircle size={13} /> Vérifie ta compréhension
      </div>
      <p className="text-sm font-medium mb-3">{data.q}</p>
      <div className="flex flex-wrap gap-2">
        {opts.map((o, i) => {
          const answered = selected !== null && selected >= 0;
          let cls = "border-border hover:border-primary/60 hover:bg-secondary/40";
          let Icon = null;
          if (answered) {
            if (o.correct) {
              cls = "border-success bg-success/12";
              Icon = CheckCircle2;
            } else if (i === selected) {
              cls = "border-destructive bg-destructive/12";
              Icon = XCircle;
            } else {
              cls = "border-border opacity-60";
            }
          }
          return (
            <button
              key={o.text}
              disabled={answered}
              onClick={() => choose(i)}
              data-testid={`diagram-quiz-option-${i}`}
              className={`inline-flex items-center gap-2 rounded-sm border px-3 py-2 text-sm transition-colors ${cls}`}
            >
              <span>{o.text}</span>
              {Icon && <Icon size={15} className={o.correct ? "text-success" : "text-destructive"} />}
            </button>
          );
        })}
      </div>
      {selected !== null && selected >= 0 && (
        <div
          data-testid="diagram-quiz-feedback"
          className={`mt-3 text-sm font-semibold ${opts[selected].correct ? "text-success" : "text-destructive"}`}
        >
          {opts[selected].correct ? "✓ Exact ! Compté dans ta progression." : "✗ Pas tout à fait — la bonne réponse est surlignée en vert."}
        </div>
      )}
    </div>
  );
}
